import React, { useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { EnvelopeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  description: string;
  category: string;
  lastModified: Date;
  timesUsed: number;
}

const AdminEmailTemplatesPage: React.FC = () => {
  const [templates] = useState<EmailTemplate[]>([
    {
      id: '1',
      name: 'Welcome Email',
      subject: 'Welcome to Patreon!',
      description: 'Sent to new users when they sign up',
      category: 'onboarding',
      lastModified: new Date('2024-01-10'),
      timesUsed: 1520,
    },
    {
      id: '2',
      name: 'Payment Failed',
      subject: 'Payment Processing Issue',
      description: 'Notifies users when payment fails',
      category: 'payments',
      lastModified: new Date('2024-01-12'),
      timesUsed: 84,
    },
  ]);

  const stats = {
    total: templates.length,
    active: templates.length,
    totalSent: templates.reduce((sum, t) => sum + t.timesUsed, 0),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Email Templates</h1>
        <Button>Create Template</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Templates</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <EnvelopeIcon className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
              <EnvelopeIcon className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Emails Sent</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSent}</p>
              </div>
              <EnvelopeIcon className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id}>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
              <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
              <p className="text-sm text-gray-500 mb-4">{template.description}</p>
              <div className="flex items-center justify-between mb-4">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {template.category}
                </span>
                <span className="text-xs text-gray-500">{template.timesUsed} sent</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary">
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button size="sm" variant="danger">
                  <TrashIcon className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminEmailTemplatesPage;
